import { useCallback, useMemo, useState } from "react";
import { MetricQuery, useApi, useLiveData } from "../api/api";
import MetricsChart from "../api/MetricsChart";
import { Link } from "react-router-dom";
import {
  AlertBanner,
  PageTitle,
  Panel,
  StatTile,
  StatusChip,
  btnSecondary,
  inputClass,
  selectClass,
} from "../api/controlPlaneUi";
import {
  formatCost,
  ModelCatalogEntry,
  Run,
  StageMetric,
  tokensOf,
} from "../api/types";

const formatNumber = (n: number): string => n.toLocaleString("en-US");
const formatSeconds = (ms: number): string => `${(ms / 1000).toFixed(1)}s`;
const shortId = (id: string): string => (id.length > 8 ? `${id.slice(0, 8)}…` : id);
const toIso = (local: string): string =>
  local ? new Date(local).toISOString() : "";

const toLocalInput = (d: Date): string => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const defaultFromLocal = (): string => {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return toLocalInput(d);
};

const OVERVIEW_METRICS_LIMIT = 500;

const OverviewPage: React.FC = () => {
  const api = useApi();
  const [from, setFrom] = useState(defaultFromLocal);
  const [to, setTo] = useState("");
  const [model, setModel] = useState("");
  const [runId, setRunId] = useState("");

  const fetchModels = useCallback(() => api.getModels(), [api]);
  const { data: modelCatalog } = useLiveData<ModelCatalogEntry[]>(
    fetchModels,
    [fetchModels]
  );

  const fetchRuns = useCallback(() => api.getRuns(200), [api]);
  const {
    data: runsData,
    error: runsError,
    loading: runsLoading,
  } = useLiveData<Run[]>(fetchRuns, [fetchRuns]);

  const metricQuery = useMemo<MetricQuery>(
    () => ({
      from: toIso(from),
      to: toIso(to),
      model,
      runId,
      limit: OVERVIEW_METRICS_LIMIT,
    }),
    [from, to, model, runId]
  );

  const fetchMetrics = useCallback(
    () => api.getMetrics(metricQuery),
    [api, metricQuery]
  );
  const {
    data: metricsData,
    error: metricsError,
    loading: metricsLoading,
  } = useLiveData<StageMetric[]>(fetchMetrics, [fetchMetrics]);

  const runs = useMemo(() => runsData ?? [], [runsData]);
  const metrics = useMemo(() => metricsData ?? [], [metricsData]);
  const error = metricsError ?? runsError;
  const loading = (runsLoading && !runsData) || (metricsLoading && !metricsData);

  const modelOptions = useMemo(() => {
    const set = new Set<string>();
    (modelCatalog ?? []).forEach((m) => m.name && set.add(m.name));
    metrics.forEach((m) => m.model && set.add(m.model));
    return Array.from(set).sort();
  }, [modelCatalog, metrics]);

  const summary = useMemo(() => {
    const totalTokens = metrics.reduce((acc, m) => acc + tokensOf(m), 0);
    const totalCost = metrics.reduce((acc, m) => acc + (m.costUsd ?? 0), 0);
    const actualCost = metrics.reduce((acc, m) => acc + (m.actualCostUsd ?? 0), 0);
    const totalDuration = metrics.reduce((acc, m) => acc + m.durationMs, 0);
    const activeRuns = runs.filter(
      (r) => r.status === "running" || r.status === "needs_rework"
    ).length;
    return {
      totalTokens,
      totalCost,
      actualCost,
      totalDuration,
      count: metrics.length,
      runCount: runs.length,
      activeRuns,
    };
  }, [metrics, runs]);

  const hasFilters = Boolean(from || to || model || runId);
  const resetFilters = () => {
    setFrom(defaultFromLocal());
    setTo("");
    setModel("");
    setRunId("");
  };

  return (
    <div className="space-y-4 max-w-[1400px]">
      <PageTitle kind="Cluster" name="overview">
        <StatusChip status="healthy" pulse />
      </PageTitle>

      {error && (
        <AlertBanner variant="error">
          {error} — is the control plane running?
        </AlertBanner>
      )}

      {loading ? (
        <Panel>
          <p className="text-sm text-slate-500 font-mono">Loading cluster state…</p>
        </Panel>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            <StatTile label="Runs" value={formatNumber(summary.runCount)} />
            <StatTile label="Active" value={formatNumber(summary.activeRuns)} />
            <StatTile label="Metrics" value={formatNumber(summary.count)} />
            <StatTile label="Tokens" value={formatNumber(summary.totalTokens)} />
            <StatTile label="Cost (list)" value={formatCost(summary.totalCost)} />
            <StatTile label="Cost (billed)" value={formatCost(summary.actualCost)} />
            <StatTile label="Duration" value={formatSeconds(summary.totalDuration)} />
          </div>

          <Panel
            title="Query filters"
            action={
              hasFilters ? (
                <button type="button" onClick={resetFilters} className={btnSecondary}>
                  Clear
                </button>
              ) : undefined
            }
          >
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
              <label className="block">
                <span className="text-[10px] font-semibold uppercase text-slate-500">
                  From
                </span>
                <input
                  type="datetime-local"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                  className={`${inputClass} mt-1`}
                />
              </label>
              <label className="block">
                <span className="text-[10px] font-semibold uppercase text-slate-500">
                  To
                </span>
                <input
                  type="datetime-local"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  className={`${inputClass} mt-1`}
                />
              </label>
              <label className="block">
                <span className="text-[10px] font-semibold uppercase text-slate-500">
                  Model
                </span>
                <select
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  className={`${selectClass} mt-1`}
                >
                  <option value="">all</option>
                  {modelOptions.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="text-[10px] font-semibold uppercase text-slate-500">
                  Run
                </span>
                <select
                  value={runId}
                  onChange={(e) => setRunId(e.target.value)}
                  className={`${selectClass} mt-1`}
                >
                  <option value="">all</option>
                  {runs.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.title || r.storySlug || shortId(r.id)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </Panel>

          <Panel title="Metrics — waterfall">
            <MetricsChart metrics={metrics} groupBy="stage" />
          </Panel>

          <Panel
            title="Tasks"
            action={
              <Link to="/orchestration/runs" className="text-sm text-[#C9A260] hover:underline font-mono">
                View all tasks →
              </Link>
            }
          >
            <p className="text-sm text-slate-600">
              {summary.runCount} run{summary.runCount === 1 ? "" : "s"} recorded
              {summary.activeRuns > 0
                ? ` · ${summary.activeRuns} active`
                : ""}
              . Use Tasks for paging, filtering, and sorting.
            </p>
          </Panel>
        </>
      )}
    </div>
  );
};

export default OverviewPage;